/** A compact, keyboard-friendly chat composer shared by both application shells. */

import SendRoundedIcon from "@mui/icons-material/SendRounded";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { KeyboardEvent } from "react";

export interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  placeholder: string;
  sendLabel: string;
  disabled?: boolean;
  isSending?: boolean;
  error?: string | null;
  maxLength?: number;
  testId?: string;
}

/** Enter sends; Shift+Enter keeps the familiar multiline-chat behaviour. */
export function ChatComposer({
  value,
  onChange,
  onSend,
  placeholder,
  sendLabel,
  disabled = false,
  isSending = false,
  error,
  maxLength = 4000,
  testId,
}: ChatComposerProps) {
  const hasMessage = value.trim().length > 0;
  const canSend = hasMessage && !disabled && !isSending;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    )
      return;
    event.preventDefault();
    if (canSend) onSend();
  };

  return (
    <Box
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        paddingTop: 1.5,
        paddingInline: { xs: 1.5, sm: 2 },
        paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <TextField
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          multiline
          minRows={1}
          maxRows={4}
          fullWidth
          slotProps={{
            htmlInput: { maxLength, "data-testid": testId },
            input: {
              sx: {
                paddingBlock: 0,
                paddingInline: 0,
                "& textarea": {
                  padding: "13px 16px",
                },
              },
            },
          }}
        />
        <IconButton
          color="primary"
          aria-label={sendLabel}
          disabled={!canSend}
          onClick={onSend}
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            backgroundColor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { backgroundColor: "primary.dark" },
            "&.Mui-disabled": {
              backgroundColor: "action.disabledBackground",
            },
          }}
        >
          <SendRoundedIcon />
        </IconButton>
      </Box>
      {error ? (
        <Typography
          variant="T3"
          role="alert"
          sx={{ color: "error.main", marginTop: 0.75, paddingInline: 0.5 }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
